
import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { motion, Reorder, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Upload, 
  Music,
  FileAudio,
  ChevronDown,
  X
} from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { LyricsManager } from '@/components/lyrics/LyricsManager';
import { hasLyrics, getSongFilename, LYRICS_CHANGED_EVENT } from '@/utils/lyricsUtils';
import { PlaylistItem } from '@/components/playlist/PlaylistItem.jsx';
import { useAudioContext } from '@/contexts/AudioContext';
import { useWelcome } from '@/contexts/WelcomeContext';

export function StreamerPlaylist({ 
  playlist, 
  currentIndex,
  isPlaying,
  onFileUpload, 
  onSongSelect, 
  onSongRemove, 
  onReorder,
  onPlay,
  onPause,
  onCollapse,
  isFloating = false
}) {
  const [reorderablePlaylist, setReorderablePlaylist] = useState(playlist);
  const [lyricsManagerOpen, setLyricsManagerOpen] = useState(false);
  const [selectedSongForLyrics, setSelectedSongForLyrics] = useState(null);
  const [lyricsStates, setLyricsStates] = useState({});
  const fileInputRef = useRef(null);
  const { resetWelcome } = useWelcome();

  const updateAllLyricsStates = useCallback(() => {
    console.log('StreamerPlaylist.jsx: updateAllLyricsStates called');
    const newLyricsStates = {};
    playlist.forEach(song => {
      const songId = getSongFilename(song) || song.id;
      const songHasLyrics = hasLyrics(song);
      newLyricsStates[songId] = songHasLyrics;
    });
    setLyricsStates(newLyricsStates);
  }, [playlist]);

  useEffect(() => {
    setReorderablePlaylist(playlist);
    updateAllLyricsStates();
  }, [playlist, updateAllLyricsStates]);

  useEffect(() => {
    if (!lyricsManagerOpen) {
      updateAllLyricsStates();
    }
  }, [lyricsManagerOpen, updateAllLyricsStates]);

  useEffect(() => {
    const handleLyricsDataChange = (event) => {
      console.log(`StreamerPlaylist.jsx: Custom lyrics event received. Detail:`, event.detail);
      updateAllLyricsStates();
    };

    const handleStorageChange = (event) => {
      if (event.key === 'sonicGrooveLyrics' || event.key === null) {
        console.log(`StreamerPlaylist.jsx: Detected localStorage (storage event). Key: "${event.key}". Updating lyrics states.`);
        updateAllLyricsStates();
      }
    };

    window.addEventListener(LYRICS_CHANGED_EVENT, handleLyricsDataChange);
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener(LYRICS_CHANGED_EVENT, handleLyricsDataChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [updateAllLyricsStates]);


  const handleFileInputChange = (e) => {
    const files = e.target.files;
    
    if (files && files.length > 0) {
      onFileUpload(files);
      e.target.value = '';
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSongClick = async (index, event) => {
    if (event.target.closest('.drag-handle') || 
        event.target.closest('.remove-button') || 
        event.target.closest('.lyrics-button')) {
      return;
    }

    const song = playlist[index];
    
    if (!song.file) {
      toast({
        title: "File Not Available",
        description: "This song file is no longer available. Please re-add the file to your playlist.",
        variant: "destructive"
      });
      return;
    }

    if (index === currentIndex) {
      // Same song - toggle play/pause
      if (isPlaying) {
        onPause();
      } else {
        await onPlay();
      }
    } else {
      // Different song - always play it if we were playing before
      const wasPlaying = isPlaying;
      
      // Select the new song first
      onSongSelect(index);
      
      // Always try to play if we were previously playing, or if user explicitly clicked
      if (wasPlaying) {
        // Use requestAnimationFrame to ensure the song selection has been processed
        requestAnimationFrame(async () => {
          try {
            await onPlay();
          } catch (error) {
            console.error('Error auto-playing new song:', error);
          }
        });
      }
    }
  };

  const handleReorder = (newOrder) => {
    setReorderablePlaylist(newOrder);
    onReorder(newOrder);
  };

  const handleAddLyrics = (song) => {
    setSelectedSongForLyrics(song);
    setLyricsManagerOpen(true);
  };

  const handleLyricsChange = (song, lyricsData) => {
    const songId = getSongFilename(song) || song.id;
    setLyricsStates(prev => ({
      ...prev,
      [songId]: !!lyricsData
    }));
    // No need to call updateAllLyricsStates here, custom event will handle it
  };
  
  const handleLyricsManagerClose = () => {
    setLyricsManagerOpen(false);
    setSelectedSongForLyrics(null);
    // updateAllLyricsStates will be triggered by the lyricsManagerOpen useEffect
  };

  const containerClass = isFloating 
    ? "h-full flex flex-col p-4 pb-0"
    : "h-full flex flex-col p-3 bg-black/50 backdrop-blur-sm";

  const headerClass = isFloating 
    ? "hidden"
    : "flex items-center justify-between mb-3";

  const controlsClass = isFloating 
    ? "flex gap-3 mb-4" 
    : "flex gap-2 mb-3";

  const buttonClass = isFloating 
    ? "flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white text-sm h-10" 
    : "flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white text-xs h-8";

  const listHeightClass = isFloating 
    ? "overflow-y-auto h-full pr-2" 
    : "overflow-y-auto h-full pr-1";

  const maxHeightStyle = isFloating 
    ? { maxHeight: 'calc(100% - 80px)' } 
    : { maxHeight: '40vh' };

  return (
    <div className={containerClass}>
      <div className={headerClass}>
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Music className="h-4 w-4" />
          Playlist ({playlist.length})
        </h3>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={onCollapse}
          className="text-white/60 hover:text-white hover:bg-white/10 h-6 w-6 p-0"
          title="Hide Playlist"
        >
          <ChevronDown className="h-3 w-3" />
        </Button>
      </div>

      <div className={controlsClass}>
        <Button
          onClick={handleUploadClick}
          className={buttonClass}
        >
          <Upload className={isFloating ? "h-4 w-4 mr-2" : "h-3 w-3 mr-1"} />
          Add Songs
        </Button>
        
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="audio/*,.mp3,.m4a,.wav,.ogg,.flac,.aac,.ost"
          onChange={handleFileInputChange}
          className="hidden"
        />
      </div>

      <div className="flex-1 overflow-hidden">
        {playlist.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-white/60">
            <FileAudio className={isFloating ? "h-12 w-12 mb-3" : "h-8 w-8 mb-2"} />
            <p className={`text-center ${isFloating ? "text-sm" : "text-xs"}`}>No songs in playlist</p>
            <p className={`text-center mt-1 ${isFloating ? "text-sm" : "text-xs"}`}>Click "Add Songs" to get started</p>
            <div className={`mt-3 flex flex-col items-center gap-1 text-center ${isFloating ? "text-sm" : "text-xs"}`}>
              <a
                href="https://ko-fi.com/bliponskrine"
                target="_blank"
                rel="noreferrer"
                className="text-cyan-400 hover:text-cyan-300 underline"
              >
                More info and full app here!
              </a>
              <button
                type="button"
                onClick={resetWelcome}
                className="text-cyan-400 hover:text-cyan-300 underline"
              >
                Relaunch the Welcome Page Popup
              </button>
            </div>
          </div>
        ) : (
          <div className={listHeightClass} style={maxHeightStyle}>
            <Reorder.Group 
              axis="y" 
              values={reorderablePlaylist} 
              onReorder={handleReorder}
              as="div"
              className={isFloating ? "space-y-2" : "space-y-1"}
            >
              {reorderablePlaylist.map((song, index) => {
                const songId = getSongFilename(song) || song.id;
                return (
                  <PlaylistItem
                    key={song.id}
                    song={song}
                    index={index}
                    currentIndex={currentIndex}
                    isPlaying={isPlaying}
                    hasLyrics={lyricsStates[songId]}
                    onSongClick={handleSongClick}
                    onSongRemove={onSongRemove}
                    onAddLyrics={handleAddLyrics}
                    isCompact={!isFloating}
                  />
                );
              })}
            </Reorder.Group>
          </div>
        )}
      </div>

      <LyricsManager
        song={selectedSongForLyrics}
        isOpen={lyricsManagerOpen}
        onClose={handleLyricsManagerClose}
        onLyricsLoaded={(lyricsData) => {
          if (selectedSongForLyrics) {
            handleLyricsChange(selectedSongForLyrics, lyricsData);
            // updateAllLyricsStates(); // Custom event will trigger this
          }
        }}
        onLyricsCleared={() => {
          if (selectedSongForLyrics) {
            handleLyricsChange(selectedSongForLyrics, null);
            // updateAllLyricsStates(); // Custom event will trigger this
          }
        }}
      />
    </div>
  );
}